import { GlobalState, DEFAULT_STATE } from "./globalstate";

// Key for storing auth settings in sessionStorage
const AUTH_SETTINGS_KEY = "netskrafl_auth_settings";

// Settings we want to persist from /login_malstadur response
export interface PersistedAuthSettings {
  userEmail: string; // CRITICAL: Used to validate settings belong to current user
  account?: string;
  netskraflUserId?: string; // Backend's internal user ID
  userNick?: string;
  firebaseApiKey?: string;
  beginner?: boolean;
  fairPlay?: boolean;
  ready?: boolean;
  readyTimed?: boolean;
  audio?: boolean;
  fanfare?: boolean;
}

const makeServerUrls = (backendUrl: string, movesUrl: string) => {
  // If the last character of the url is a slash, cut it off,
  // since path URLs always start with a slash
  const cleanupUrl = (url: string) => {
    if (url.length > 0 && url[url.length - 1] === "/") {
      url = url.slice(0, -1);
    }
    return url;
  };
  return {
    serverUrl: cleanupUrl(backendUrl),
    movesUrl: cleanupUrl(movesUrl),
  };
}

export const makeGlobalState = (overrides: Partial<GlobalState>): GlobalState => {
  const state: GlobalState = {
    ...DEFAULT_STATE,
    ...overrides,
  };
  const stateWithUrls = { ...state, ...makeServerUrls(state.serverUrl, state.movesUrl) };
  // Apply any persisted authentication settings from sessionStorage
  return applyPersistedSettings(stateWithUrls);
}

// Save authentication settings to sessionStorage
export const saveAuthSettings = (settings: PersistedAuthSettings): void => {
  if (!settings) {
    clearAuthSettings();
    return;
  }
  try {
    // Filter to only include properties defined in PersistedAuthSettings interface
    const filteredSettings: PersistedAuthSettings = {
      userEmail: settings.userEmail, // Required field
    };

    // Only add optional fields if they are defined
    if (settings.account !== undefined) filteredSettings.account = settings.account;
    if (settings.netskraflUserId !== undefined) filteredSettings.netskraflUserId = settings.netskraflUserId;
    if (settings.userNick !== undefined) filteredSettings.userNick = settings.userNick;
    if (settings.firebaseApiKey !== undefined) filteredSettings.firebaseApiKey = settings.firebaseApiKey;
    if (settings.beginner !== undefined) filteredSettings.beginner = settings.beginner;
    if (settings.fairPlay !== undefined) filteredSettings.fairPlay = settings.fairPlay;
    if (settings.ready !== undefined) filteredSettings.ready = settings.ready;
    if (settings.readyTimed !== undefined) filteredSettings.readyTimed = settings.readyTimed;
    if (settings.audio !== undefined) filteredSettings.audio = settings.audio;
    if (settings.fanfare !== undefined) filteredSettings.fanfare = settings.fanfare;

    // Only save if we have actual settings to persist
    if (Object.keys(filteredSettings).length > 1) {
      sessionStorage.setItem(AUTH_SETTINGS_KEY, JSON.stringify(filteredSettings));
    } else {
      clearAuthSettings();
    }
  } catch (error) {
    // SessionStorage might be unavailable or full
    console.warn("Could not save auth settings to sessionStorage:", error);
  }
};

// Retrieve authentication settings from sessionStorage
const loadAuthSettings = (): PersistedAuthSettings | null => {
  try {
    const stored = sessionStorage.getItem(AUTH_SETTINGS_KEY);
    if (stored) {
      return JSON.parse(stored) as PersistedAuthSettings;
    }
  } catch (error) {
    // SessionStorage might be unavailable or data might be corrupted
    console.warn("Could not load auth settings from sessionStorage:", error);
  }
  return null;
};

// Clear authentication settings from sessionStorage
export const clearAuthSettings = (): void => {
  try {
    sessionStorage.removeItem(AUTH_SETTINGS_KEY);
  } catch (error) {
    console.warn("Could not clear auth settings from sessionStorage:", error);
  }
};

// Apply persisted settings to a GlobalState object
const applyPersistedSettings = (state: GlobalState): GlobalState => {
  const persisted = loadAuthSettings();
  if (!persisted) {
    return state;
  }

  // CRITICAL SECURITY CHECK: Only apply persisted settings if they belong to the current user
  // This prevents data leakage between different users in the same browser session
  if (persisted.userEmail !== state.userEmail) {
    // Different user detected - clear the old user's settings
    clearAuthSettings();
    return state;
  }

  // Apply persisted settings, but don't override values explicitly passed in props
  return {
    ...state,
    // Only apply persisted values if current values are defaults
    account: persisted.account || state.account || state.netskraflUserId,
    netskraflUserId: persisted.netskraflUserId || state.netskraflUserId,
    // userNick is special: the container provides a "guess", but the backend login
    // returns the authoritative nickname, which should persist across re-initializations
    userNick: persisted.userNick || state.userNick,
    firebaseApiKey: state.firebaseApiKey || persisted.firebaseApiKey || state.firebaseApiKey,
    beginner: persisted.beginner ?? state.beginner,
    fairPlay: persisted.fairPlay ?? state.fairPlay,
    ready: persisted.ready ?? state.ready,
    readyTimed: persisted.readyTimed ?? state.readyTimed,
    audio: persisted.audio ?? state.audio,
    fanfare: persisted.fanfare ?? state.fanfare,
  };
};
